import { Model } from '@mode-vegas/vegas';
import type * as ngRouter from '@uirouter/angularjs';
import * as angular from 'angular';
import Backoff from 'backo';
import { difference, keyBy, map as lodashMap } from 'lodash';
import { Observable, combineLatest, lastValueFrom } from 'rxjs';
import { filter, first, map, merge, switchMap, withLatestFrom } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { FeatureFlag, FlamingoTypes, ReportTypes } from '@mode/shared/contract-common';
import { getCsrfToken, getInitialFlagValue } from '@mode/shared/util-js';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';

import { DatasourceFacade } from '@mode/accounts/domain';
import {
  AccountResource,
  Dataset,
  DatasetResource,
  QueryResource,
  QueryResultResource,
  QueryRunResource,
  QueryRunResourceHelpers,
  QueryRunViewResource,
  ReportRunResource,
  formSubmit,
} from '@mode/legacy/data-access-model-js';
import { ImmutableRecordSet, ModelState, QueryRunState, QueryState } from '@mode/legacy/types-app-state';
import { MetricFacade } from '@mode/metrics/domain';
import { MetricsActions } from '@mode/metrics/domain';
import { ReportEditorFacade } from '@mode/report-editor/domain';
import { ReportFilterFacade, ReportViewerFacade } from '@mode/report-viewer/domain';
import { DataViewTypes, RUNNING_QUERY_STATES } from '@mode/shared/contract-common';
import { ReportDomain } from '@mode/shared/contract-common';
import { CappieReporterService } from '@mode/shared/util-cappie-reporter';
import { concur } from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade, SwitchViewActions } from '@mode/switch-views/contract';
import { isReportViewerRoute } from '~/lib/router-helpers';
import { EditorActionType } from '../actions/editor-actions';
import { ReportLayoutActions } from '../actions/report-layout.actions';
import { LoadQueryRunView, ReportRunActionType } from '../actions/report-run.actions';
import { StoreConnector } from '../common/store-connector';
import { VizTypes } from '../common/types';
import { BugsnagHelpers } from '../lib/bugsnag-helpers';
import { VegasHelpers } from '../lib/vegas-helpers';
import { ModeApp } from '../reducers/index';
import {
  accountSelector,
  highlightedQueryTextSelector,
  transientSelectedQueryTokenSelector,
} from '../reducers/selectors';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';
import ChartDataService from './chart-data-service';
import DatasetService from './dataset-service';
import EditorService from './editor-service';
import { QueryDataService } from './query-data-service';
import { ReportDataService } from './report-data-service';
import { ReportFilterService } from './report-filter-service';
import { ReportSaveService } from './report-save-service';
import { ReportStateService } from './report-state-service';
import ViewService from './view-service';
import { DataRunClientService } from '@mode/shared/data-access-webapp';
import { getEmbedded, getTokenFromModeLink, ModeResourceHelper } from '@mode/shared/util-halogen';
import { Includes } from 'libs/shared/contract-common/src/lib/mode-api';
import { reportCache, reportRunCache } from '@mode/shared/data-access-webapp';
import { NotebookSelectors } from '~/selectors/notebook.selectors';

const minPollInterval = 50;
const maxPollInterval = 5000;
const pollFactor = 1.2;

export const QUERY_RUN_RESOURCE_PROPERTIES = [
  'completed_at',
  'created_at',
  'data_source_id',
  'error_code',
  'error_message',
  'error_type',
  'help_url',
  'id',
  'limit',
  'max_result_bytes',
  'parameters',
  'query_created_at',
  'query_name',
  'query_token',
  'raw_source',
  'rendered_source',
  'state',
  'statement_annotation',
  'token',
];
export class ReportRunDataService {
  static $inject = [
    '$q',
    '$log',
    '$rootScope',
    '$window',
    '$timeout',
    '$state',
    'StoreService',
    'RxStoreService',
    'actions$',
    'ReportSaveService',
    'ReportDataService',
    'QueryDataService',
    'ToastService',
    'AnalyticsService',
    'ChartDataService',
    'DatasetService',
    'ReportFilterService',
    'ViewService',
    'ReportStateService',
    'ReportRunModel',
    'ModeCollection',
    'QueryRunModel',
    'ReportEditorFacade',
    'DataViewFacade',
    'DatasourceFacade',
    'EditorService',
    'ReportViewerFacade',
    'ReportFilterFacade',
    'MetricFacade',
    'CappieReporterService',
    'ObservabilityService',
    'DataRunClientService',
    'ModeResource',
  ];

  timeoutPromise: angular.IPromise<void>;
  limits: { [token: string]: boolean } = {}; // HACK: stores limit checkbox states of all queries whenever a single query is run

  // Maintains a list of all query results that have been populated.
  // Used for knowing whether to pull a result again if it's in the succeeded state
  loadedQueryResults = new Map<string, { queryRunResultPromise: Promise<any>; queryRunToken: string }>();

  // The current/last report run that is/was run. Used for stopping the polling of a report run that's been replaced.
  currentRunToken: string;

  private reportRunsRequireResults = true;
  // [DO NOT USE] Legacy ReportModel object from the old, pre-rx-store world (use client.reportResource in rx state instead)
  private reportModel: any;

  private isExportDatasetsEnabled = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

  constructor(
    private $q: angular.IQService,
    private $log: angular.ILogService,
    private $root: angular.IRootScopeService,
    private $window: angular.IWindowService,
    private $timeout: angular.ITimeoutService,
    private $state: ngRouter.StateService,
    private StoreService: StoreConnector<ModeApp>,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private reportSaveService: ReportSaveService,
    private reportDataService: ReportDataService,
    private queryDataService: QueryDataService,
    private toastService: ToastService,
    private analyticsService: AnalyticsService,
    private chartDataService: ChartDataService,
    private datasetService: DatasetService,
    private reportFilterService: ReportFilterService,
    private viewService: ViewService,
    private reportStateService: ReportStateService,
    private ReportRunModel,
    private ModeCollection,
    private QueryRunModel: any,
    private editorFacade: ReportEditorFacade,
    private dataViewFacade: DataViewFacade,
    private datasourceFacade: DatasourceFacade,
    private editorService: EditorService,
    private reportViewerFacade: ReportViewerFacade,
    private reportFilterFacade: ReportFilterFacade,
    private metricFacade: MetricFacade,
    private cappieReporterService: CappieReporterService,
    private observabilityService: ObservabilityService,
    private dataRunClientService: DataRunClientService,
    private ModeResource
  ) {
    // This creates scenarios with false positives, but that's less risky than
    this.reportViewerFacade.reportLayoutNoEmpty$
      .pipe(
        switchMap(() =>
          combineLatest([
            this.reportViewerFacade.hasScriptTagGf$,
            this.reportViewerFacade.v1ChartTokensInLayout$,
            this.reportViewerFacade.tableTokensInLayout$,
          ]).pipe(
            map(([hasScript, v1ChartTokensInLayout, tableTokensInLayout]) => {
              this.reportRunsRequireResults =
                hasScript || !!v1ChartTokensInLayout.length || !!tableTokensInLayout.length;
            })
          )
        )
      )
      .subscribe();

    actions$.pipe(ofType(ReportRunActionType.CancelPollReportRun)).subscribe(() => {
      $timeout.cancel(this.timeoutPromise);
    });

    // This action fires when a user executes a report run
    actions$
      .pipe(
        ofType(ReportRunActionType.ExecuteReportRun),
        withLatestFrom(
          this.store.select('client', 'selectedReport', 'queries'),
          this.store.select('client', 'selectedReport', 'queryRuns'),
          this.store.select(transientSelectedQueryTokenSelector),
          this.store.select(accountSelector)
        ),
        switchMap(
          ([action, queryStates, queryRuns, selectedQueryToken, account]) =>
            this.createReportRun(action.runningQueryToken, action.selectedText, queryStates, queryRuns, account).catch(
              () => {}
            ) // exceptions are only thrown so that the ExecutePreviewTableQueryRunAction side-effect can track them
        )
      )
      .subscribe();

    // This action fires on Click-to-Preview
    actions$
      .pipe(
        ofType(ReportRunActionType.ExecutePreviewTableQueryRun),
        withLatestFrom(
          store.select(SelectedReportSelectors.getQueries),
          store.select(SelectedReportSelectors.getQueryRuns),
          store.select(accountSelector)
        ),
        switchMap(([action, queryStates, queryRunStates, account]) =>
          this.createReportRun(action.runningQueryToken, action.selectedText, queryStates, queryRunStates, account)
            .then((results) => {
              const queries = results._resource
                .embedded('report')
                .embedded('queries')
                .embedded('queries') as QueryResource[];
              const queryRuns = results._resource.embedded('query_runs').embedded('query_runs') as QueryRunResource[];

              const mappedQuery = queries.find(
                (q) => action.runningQueryToken && q.mapping_id === action.runningQueryToken
              );
              const query = mappedQuery || queryStates.getRecord(action.runningQueryToken).resource;
              const queryRun = queryRuns.find((run) => run.query_token === query.token);

              this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.TablePreviewClicked, {
                query_id: query.id,
                query_run_id: queryRun.id,
                data_source_id: queryRun.data_source_id,
                table_schema: action.tableSchema,
                table_name: action.tableName,
                button_location: action.buttonLocation,
                search_query: action.searchQuery,
              });
            })
            .catch((e) => {
              const query = queryStates.getRecord(action.runningQueryToken).resource;
              this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.TablePreviewClicked, {
                query_id: query.id,
                error: e.data,
                data_source_id: query.data_source_id,
                table_schema: action.tableSchema,
                table_name: action.tableName,
                button_location: action.buttonLocation,
                search_query: action.searchQuery,
              });
            })
        )
      )
      .subscribe();

    // This action should be fired when first loading a report page
    actions$
      .pipe(
        ofType(ReportRunActionType.PollReportRun),
        switchMap((action) => {
          // Clear this in case of a fullscreen refresh
          this.loadedQueryResults.clear();
          recordEvent('legacy-poll-report-run', 'poll-run', { traceId: action.token });

          // Get Report
          // TODO: pollLoadedReportRun should be refactored to use the report run resource
          // and then we don't need to get the report run twice here
          return this.ReportRunModel.find({
            token: action.token,
            useEntryPath: true,
          })
            .then((reportRun) => {
              return this.pollLoadedReportRun(reportRun);
            })
            .catch((err) => {
              this.store.dispatch({
                type: ReportRunActionType.PollReportRunFailed,
                token: action.token,
                error: err,
              });

              recordEvent('legacy-poll-report-run', 'poll-run-failed', { traceId: action.token, err });
            });
        })
      )
      .subscribe();

    // Cancel Query Run
    actions$
      .pipe(
        ofType(ReportRunActionType.CancelQueryRun),
        withLatestFrom(store.select('client', 'selectedReport', 'queryRuns')),
        switchMap(([action, queryRuns]) => {
          if (
            queryRuns &&
            queryRuns.hasRecord(action.token) &&
            queryRuns.getRecord(action.token).resource &&
            queryRuns.getRecord(action.token).resource.hasForm('cancel')
          ) {
            return queryRuns
              .getRecord(action.token)
              .resource.forms('cancel')
              .$submit()
              .then((qrResource) => {
                datasetService.queryRuns[action.token].state = 'cancelled';
                datasetService.datasets[action.token] = { query_token: action.token, state: 'cancelled' };
                this.loadedQueryResults[action.token] = true; // Don't attempt to load query results for cancelled queries

                this.dispatchQueryRunSuccess(qrResource, action.token);
              })
              .catch((e) => {
                console.error('cancelling failed', e);
                this.toastService.error('Run succeeded before cancellation request could complete');
              });
          } else {
            if (!queryRuns.hasRecord(action.token)) {
              return this.$q.reject('Unable to find query run to cancel');
            } else {
              if (queryRuns.getRecord(action.token).resource != null) {
                return this.$q.reject('Query run does not have cancel link');
              } else {
                return this.$q.reject('Query run not loaded yet');
              }
            }
          }
        })
      )
      .subscribe();

    // Run selected text, after having selected query to preview a table
    actions$
      .pipe(
        ofType(EditorActionType.RunPreviewTableQuery),
        withLatestFrom(store.select(highlightedQueryTextSelector), store.select(transientSelectedQueryTokenSelector))
      )
      .subscribe(([action, selectedText, queryToken]) => {
        this.store.dispatch({
          type: ReportRunActionType.ExecutePreviewTableQueryRun,
          runningQueryToken: queryToken,
          selectedText,
          tableSchema: action.payload.tableSchema,
          tableName: action.payload.tableName,
          buttonLocation: action.payload.buttonLocation,
          searchQuery: action.payload.searchQuery,
          runningQueryLimit: this.limits[queryToken], // this is the only reason this action even
          // exists in the first place. Once the limits checkboxes are properly moved into the
          // rx state store, report run creation can subscribe directly to RunPreviewTableQueryAction
        });
      });

    actions$
      .pipe(
        ofType(ReportRunActionType.LoadQueryRunSuccess),
        map((action) => {
          const switchView = action.resource.embedded('view') as QueryRunViewResource;
          return {
            queryToken: action.resource.query_token,
            switchViewToken: switchView?.token,
          };
          // TODO: what should happen if the view is missing?
        }),
        filter(({ switchViewToken }) => switchViewToken != null)
      )
      .subscribe(({ queryToken, switchViewToken }) =>
        this.dataViewFacade.setSwitchViewRef(queryToken, switchViewToken)
      );

    /*
     *After a cell export is complete, refreshEditorViewsSuccess fetches updated DataViews and reportRun
     *We get the reportRun from the reportCache, set it to the reportRunCache for it to be used anywhere else
     */
    actions$
      .pipe(
        ofType(SwitchViewActions.refreshEditorViewsSuccess.type),
        withLatestFrom(this.store.select(NotebookSelectors.selectedNotebookToken)),
        map(([action, notebookToken]) => {
          const reportResource = reportCache.get(action.reportToken);
          const reportRun = ModeResourceHelper.resourceToModel(
            new this.ModeResource(getEmbedded(reportResource, 'latest_report_run')),
            this.ReportRunModel.model
          );
          const reportRunResource = reportRun._resource as ReportRunResource;
          reportRunCache.set(reportRun.token, reportRunResource as any);

          this.store.dispatch({
            type: ReportRunActionType.RefreshReportRunAfterCellExport,
            reportRunResource,
            notebookToken,
            exportedDatasetToken: action.exportedDatasetToken,
          });
        })
      )
      .subscribe();

    /*
     *RefreshReportRunAfterCellExport polls the loaded report run after the report run is hydrated in the state
     *This ensures the dataRuns are loaded after the loaded report run is saved
     *It won't really poll as the dataRuns in the backend are already completed, it just helps fetch these in the UI
     */
    actions$
      .pipe(
        ofType(ReportRunActionType.RefreshReportRunAfterCellExport),
        map((action) => {
          const reportRunModel = new this.ReportRunModel.model(action.reportRunResource);
          this.refreshLoadedReportRun(reportRunModel, action.exportedDatasetToken);
        })
      )
      .subscribe();

    actions$
      .pipe(
        ofType(MetricsActions.createMetricQuerySuccess),
        switchMap(({ query }) =>
          actions$.pipe(
            ofType(ReportRunActionType.LoadQueryRunSuccess),
            filter(({ mapping_id }) => mapping_id === query.token),
            filter(({ resource }) => {
              switch (resource.state) {
                case DataViewTypes.QueryRunStatus.Failed:
                case DataViewTypes.QueryRunStatus.Cancelled:
                case DataViewTypes.QueryRunStatus.Succeeded:
                case DataViewTypes.QueryRunStatus.Completed: {
                  return true;
                }
                default: {
                  return false;
                }
              }
            }),
            map((run) => ({
              metricToken: query.dbt_metric_token,
              queryToken: run.resource.token,
            })),
            first()
          )
        )
      )
      .subscribe(({ metricToken, queryToken }) => this.metricFacade.metricQueryRunCompleted(metricToken, queryToken));
  }

  private detectOutOfSyncQueries(queries: ImmutableRecordSet<QueryState>) {
    // Check if queries are out of sync with their respective Ace sessions
    const outOfSync = queries.getRecords().filter((query) => {
      const session = this.editorService.getSession(query.token);
      if (session && session.getValue().replace(/\r/g, '') !== query.raw_query.replace(/\r/g, '')) {
        return true;
      }

      return false;
    });

    if (outOfSync.length > 0) {
      BugsnagHelpers.notifySafely(new Error('Queries out of sync'), (event) => {
        event.groupingHash = 'QueriesOutOfSyncError';

        event.addMetadata('editors', {
          queryTokens: outOfSync.map((q) => q.token),
          editorQueries: outOfSync.map((q) => this.editorService.getSession(q.token).getValue().substring(0, 10000)),
          rawQueries: outOfSync.map((q) => q.raw_query.substring(0, 10000)),
        });
      });
    }
  }

  private createReportRun(
    runningQueryToken: string,
    selectedText: string,
    queryStates: ImmutableRecordSet<QueryState>,
    queryRuns: ImmutableRecordSet<QueryRunState>,
    account: AccountResource
  ) {
    // Reset any loaded query results that need to be refetched
    const traceName = ObservabilityEvents.QUERY_RUN;
    this.store
      .select(SelectedReportSelectors.selectedQueryRunRecord)
      .pipe(first())
      .subscribe((selectedQueryRunRecord) => {
        this.observabilityService.startTrace(traceName, {
          startTime: Date.now(),
          attributes: {
            queryToken: runningQueryToken ?? selectedQueryRunRecord.query_token,
            queryRunToken: runningQueryToken ? selectedQueryRunRecord.token : 'N/A',
            datasourceToken: queryRuns.getRecord(runningQueryToken)?.datasourceToken ?? '',
            orgToken: !account.user ? 'N/A' : account.token,
            userToken: account.user ? account.token : 'N/A',
            reportToken: this.reportModel.token,
            queryRunSource: 'manual', // there's no way for the FE to do anything besides a manual run
            runningAllQueries: !runningQueryToken, // if a token is provided, it means we're running a single query
          },
        });
        this.observabilityService.startChildSpan(ObservabilityEvents.CREATE_REPORT_RUN, traceName);
      });

    if (!runningQueryToken) {
      this.loadedQueryResults.clear();
    } else {
      this.loadedQueryResults.delete(runningQueryToken);
    }

    this.$timeout.cancel(this.timeoutPromise);

    // Null out here so there's less chance that a query run returns while waiting for a run to queue
    this.currentRunToken = null;
    this.detectOutOfSyncQueries(queryStates);

    // pins are of type ReportPinModelObject
    let pins = this.reportModel.pins;
    if (this.reportModel._new_report) {
      pins = lodashMap(this.$root.pins, (pin: any) => ({
        schemadonna_id: pin.schemadonna_id,
        pinned_type: pin.pinned_type,
        pinned_id: pin.pinned_id,
        token: pin.token,
      }));
    }

    // If on a datasets route, we pass the DatasetReport report type, otherwise Report.
    // Note - This is a workaround for what would ideally be a more explicit distinct creation
    //   flow between Reports and DatasetReports. We likely may want to adjust this when these
    //   flows are brought fully into the Angular context.
    const reportType = this.$state.includes('application.datasetReports')
      ? ReportTypes.ReportType.DatasetReport
      : ReportTypes.ReportType.Report;

    const params = {
      'report.report_pins[]': pins,
      'report.queries[]': queryStates
        .getRecords()
        .filter((q) => q != null)
        .map((query) => {
          // Set limit to whatever its last run limit was, or true if never run previously
          let limit = queryRuns.hasRecord(query.token) ? queryRuns.getRecord(query.token).limit : true;

          // When running a single query, use the limit that's set from the runner toolbar
          if (runningQueryToken && runningQueryToken === query.token) {
            limit = this.limits[runningQueryToken];

            // Attmept to keep default datasource in sync with backend
            this.datasourceFacade.updateDefaultDatasource(query.data_source_id);
          }

          const responseParams = {
            create_query_run: runningQueryToken ? query.token === runningQueryToken : true,
            limit: limit,
            data_source_id: query.data_source_id,
            name: query.name,
            raw_query: query.raw_query,
            token: query.token,
            mapping_id: '',
            selected_query: '',
            selected_run: false,
            // @todo: we may only want to include this if _new_query=true such that
            // each run of a metric query does not attempt to create a new chart
            // charts: query.charts,
            dbt_metric_id: query.dbt_metric_id,
          };

          // Remove metrics related properties if they are not being used for now
          if (!Number.isFinite(query.dbt_metric_id)) {
            // delete responseParams.charts;
            delete responseParams.dbt_metric_id;
          }

          if (query.resource._new_query) {
            responseParams.mapping_id = query.token;
            delete responseParams.token;
          }

          if (selectedText && runningQueryToken === query.token) {
            responseParams.selected_query = selectedText;
            responseParams.selected_run = true;
          }
          return responseParams;
        }),
      includes: [
        'new_report_run_email',
        'new_report_run_slack_message',
        {
          report: [
            'new_python_notebook',
            'new_report_schedule',
            'new_report_filter',
            'new_report_subscription',
            'new_embed_key',
            'new_python_visualization',
            'new_space_link',
            'report_filters',
            { local_switch_views: { switch_views: ['last_data_run', 'new_visualization'] } },
            {
              report_pins: 'pinned',
            },
            {
              queries: {
                queries: [
                  'new_chart',
                  {
                    charts: {
                      charts: ['color_palette'],
                    },
                  },
                  'new_query_table',
                  'query_tables',
                ],
              },
            },
            {
              local_datasets: {
                local_datasets: [
                  'new_chart',
                  {
                    charts: {
                      charts: ['color_palette'],
                    },
                  },
                  'new_query_table',
                  'query_tables',
                ],
              },
            },
          ],
        },
      ],
    };

    // Fill with report model defaults/current values, this will override any values set above with values from the model
    angular.extend(params, this.reportModel.toParams());

    // We need to manually override the `reportType` here to handle DatasetReport creation
    params['report.type'] = reportType;

    // save current time for tracking query time
    const currentTime = new Date();
    const newReport = this.reportModel._new_report; // storing here because model can auto-update

    return this.ReportRunModel.create({
      account: account.username,
      report: this.reportModel.token,
      params: params,
      useEntryPath: true,
    })
      .then((result) => {
        this.trackRunEvent(this.reportModel.public);
        this.observabilityService.endChildSpan(ObservabilityEvents.CREATE_REPORT_RUN);

        this.store.dispatch({
          type: ReportRunActionType.ExecuteReportRunSuccess,
          reportRunResource: result._resource,
          runningQueryToken,
          isNew: newReport,
        });

        const queries = this.isExportDatasetsEnabled
          ? (result._resource
              .embedded('report')
              .embedded('local_datasets')
              .embedded('local_datasets') as QueryResource[])
          : (result._resource.embedded('report').embedded('queries').embedded('queries') as QueryResource[]);

        // Update redux queryInfo token if it's been remapped
        const mappedQueries = queries.filter((q) => runningQueryToken && q.mapping_id);
        if (mappedQueries.length > 0) {
          mappedQueries.forEach((query) => {
            this.viewService.remapQuery(query);
          });
        }

        // HACK: need to keep rxStore queries up to date so call a batch update here with embedded queries
        this.queryDataService.batchUpdateQueries(queries);

        // Temporary action call to sync temp local query dataset with new queryToken so we can
        // update it by matchihg the query token once the query run has completed. This handles both
        // the case where a new query is just run, but also the case where many new
        // queries may have been created and then a *different* query was run.
        const tempLocalQueriesToUpdate = queries.filter((q) => q.mapping_id);
        tempLocalQueriesToUpdate.forEach((query) => {
          this.dataViewFacade.updateTemporaryLocalQueryToken(query.mapping_id, query.token);
        });

        // Editor Polling - Kick off the editor polling for datasets flow.
        const reportRunToken: string = result.token;
        const reportToken: string = result._resource.embedded('report').token;
        const localSwitchViews = result._resource
          .embedded('report')
          .embedded('local_switch_views')
          .embedded('switch_views');

        // Filter out switchViews that Dataset Creators that have never been run so we don't try to poll switchViews
        // that cannot be polled and will just return an error.
        const localSwitchViewsWithRuns = localSwitchViews.filter((switchViewResource) =>
          switchViewResource.embedded('last_data_run')
        );
        const switchViewTokens: string[] = localSwitchViewsWithRuns.map((switchView) => switchView.token);

        // If single query was run, set that query to pending. Else, all queries were run, set all local to pending.
        if (runningQueryToken) {
          this.dataViewFacade.setPendingDatasetResultByQueryToken(runningQueryToken);
        } else {
          this.dataViewFacade.setPendingAllLocalDatasetResults();
        }
        // Clear out switch view resources with these tokens. The polling flow assumes no current switch view in HAL cache.
        this.dataViewFacade.startEditorPolling(reportToken, reportRunToken, switchViewTokens);
        // END Editor Polling

        this.pollLoadedReportRun(result, runningQueryToken).then((rr) => {
          rr.localCreatedAt = currentTime;
          rr.report()
            .then((r) => r.queries())
            .then((queryCollection) => queryCollection.elements())
            .then((queryModels: any[]) => {
              // Hacky stuff to support existing code. Could be refactored once dataset service and others are removed.
              this.$root.reportRun = rr;
              this.reportDataService.updateIfReportNeedsUpdate();
              this.reportSaveService.copyReport(this.reportSaveService.report);

              // At this point, mapping_id is not set and queryStates will be out of sync with the resources
              const newQueries = queryStates.getRecords().filter((q) => q.resource._new_query);
              const queriesWithNewTable = queryModels.filter((q) => newQueries.some((nq) => nq.token === q.mapping_id));
              queriesWithNewTable.forEach((newQuery) => {
                const tables = newQuery._resource.embedded('query_tables').embedded('query_tables');
                if (tables.length) {
                  this.chartDataService.loadEmbeddedTables(newQuery.token, tables);
                  this.store.dispatch(new ReportLayoutActions.AddViz(tables[0].token, null, VizTypes.Table));
                }
              });

              this.$root.focused = false;
            });
        });

        return result;
      })
      .catch((e) => {
        const reg = /Number of queries in this report exceeds/i;
        const queryErrorCause = reg.test(e.data.message)
          ? ReportDomain.ReportRunFailCause.TooManyQueries
          : ReportDomain.ReportRunFailCause.Unknown;

        this.store.dispatch({
          type: ReportRunActionType.ExecuteReportRunFailed,
          runningQueryToken: runningQueryToken,
          error: e,
          errorCause: queryErrorCause,
        });

        if (e !== 'Queries executing') {
          this.toastService.error(
            e.data && e.data.message ? e.data.message : 'Problem running report. Please try again.'
          );
        }

        throw e;
      });
  }

  public setReportModel(report: any) {
    this.reportModel = report;
  }

  public pollReportRun(token: string): void {
    this.store.dispatch({
      type: ReportRunActionType.PollReportRun,
      token: token,
    });
  }

  public executeReportRun(runningQueryToken: string, selectedText: string) {
    this.store.dispatch({
      type: ReportRunActionType.ExecuteReportRun,
      runningQueryToken: runningQueryToken,
      selectedText: selectedText,
      runningQueryLimit: this.limits[runningQueryToken],
    });
  }

  // TODO: Once query limit model is fixed, remove this hack
  // This is called from the runner controller to keep the limit checkbox states up to date so it can be used
  // in single query executions.
  public setQueryLimits(limits: ReportRunDataService['limits']) {
    this.limits = limits;
  }

  // replacement for reportRun.queryRuns()
  // this will fetch dataRuns (local and exported datasets), this method will create models out of them for it to be used in pollLoadedReportRun
  private loadDataRuns(reportRunResource: ReportRunResource, includes: Includes) {
    const accountToken = getTokenFromModeLink(reportRunResource.links('account'));
    const reportToken = getTokenFromModeLink(reportRunResource.links('report'));
    const reportRunToken = reportRunResource.token;
    return lastValueFrom(
      this.dataRunClientService.loadDataRuns(accountToken, reportToken, reportRunToken, includes).pipe(
        map((dataRuns) => {
          return dataRuns.map((dataRun) => {
            const dataRunModel = ModeResourceHelper.resourceToModel(
              new this.ModeResource(dataRun),
              this.QueryRunModel.model
            );
            return dataRunModel;
          });
        })
      )
    );
  }

  public pollLoadedReportRun(reportRunModel: any, queryToken?: string): angular.IPromise<any> {
    const deferred = this.$q.defer<ReportRunResource>();
    const backoff = new Backoff({ min: minPollInterval, max: maxPollInterval, factor: pollFactor });
    const renderingReporter = this.cappieReporterService.progressEventAPI('mode-datasets-downloading');
    this.currentRunToken = reportRunModel.token;

    // Setup a query run cache to avoid dispatching LoadQueryRunSuccess redundantly between polls
    const polledQueryRuns: {
      [runToken: string]: string;
    } = {};

    const queryRunFields = difference(QUERY_RUN_RESOURCE_PROPERTIES, ['raw_source', 'rendered_source']);

    const poll = (rr: any) => {
      const dataRunReq = this.isExportDatasetsEnabled
        ? this.loadDataRuns(reportRunModel._resource, {
            view: ['attrs', 'new_attr'],
            query_runs: { only: queryRunFields },
          })
        : rr
            .queryRuns({ includes: { view: ['attrs', 'new_attr'], query_runs: { only: queryRunFields } } })
            .then(this.ModeCollection.elements);
      dataRunReq
        .then((queryRuns: any[]) => {
          // Stop polling and just quit if another run has been requested
          if (this.currentRunToken !== rr.token) {
            recordEvent('legacy-poll-report-run', 'cancel-for-new-run', {
              traceId: this.currentRunToken,
              newRunToken: rr.token,
            });
            return;
          }

          recordEvent('legacy-poll-report-run', 'query-runs-loaded', { traceId: rr.token, queryRuns });
          const completed = queryRuns.every((qr) => RUNNING_QUERY_STATES.indexOf(qr.state) === -1);

          // Limit legacy result/dataset calls for safety. Similar behavior to flamingo requests.
          const throttledLoadResult = concur((resource: QueryRunResource) => this.loadQueryRunResult(resource), 3);
          queryRuns.forEach((queryRunModel) => {
            // Only update query runs for queries that are executing
            const resource = queryRunModel._resource as QueryRunResource;
            const isFinished = RUNNING_QUERY_STATES.indexOf(resource.state) < 0;
            const queryResult = this.loadedQueryResults.get(resource.query_token);
            const hasBeenRerun = queryResult && queryResult.queryRunToken !== resource.token;

            if (polledQueryRuns[resource.token] !== resource.state) {
              recordEvent('legacy-poll-report-run', 'query-run-loaded', { traceId: rr.token, queryRun: resource });
              this.dispatchQueryRunSuccess(resource, resource.query_token);
              polledQueryRuns[resource.token] = resource.state;
            }
            if (!this.reportRunsRequireResults && isReportViewerRoute(this.$state.current.name)) {
              this.store.dispatchThunk(
                SelectedReportSelectors.createQueryResourceSelector(resource.query_token),
                (query) => ({
                  type: ReportRunActionType.ReportRoutesNoScriptOrLegacy,
                  queryRun: resource,
                  queryResource: query,
                })
              );
            } else if (isFinished && (!queryResult || hasBeenRerun)) {
              this.loadedQueryResults.set(resource.query_token, {
                queryRunResultPromise: throttledLoadResult(resource),
                queryRunToken: resource.token,
              });
            }
          });

          this.datasetService.queryRuns = keyBy(queryRuns, 'query_token');
          this.datasetService.dismissed = {};

          if (completed) {
            queryRuns.forEach((qr) => {
              this.queryDataService.updateQueryLimit(qr.query_token, qr.limit);
            });

            const resultPromises = Array.from(this.loadedQueryResults.values()).map((r) => r.queryRunResultPromise);

            this.$q.all(resultPromises).then((results) => {
              this.reportFilterService.updateModels();
              this.reportStateService.setDatasets(this.datasetService.datasets);
              this.StoreService.dispatch({ type: 'REPORT_RUN_DATA_LOADED' });
              this.datasetService.loadRawDatasets(results.filter((ds) => ds != null));
            });

            recordEvent('legacy-poll-report-run', 'poll-complete', {
              traceId: rr.token,
            });
            this.store.dispatch({
              type: ReportRunActionType.PollReportRunComplete,
              token: rr.token,
            });
            renderingReporter.end('finished downloading datasets');
            deferred.resolve(rr);
            this.observabilityService.endChildSpan(ObservabilityEvents.LEGACY_POLLING_LOOP);
          } else {
            renderingReporter.progress('polling for datasets');
            this.timeoutPromise = this.$timeout(() => poll(rr), backoff.duration());
          }
        })
        .catch((err) => {
          renderingReporter.end('error downloading datasets');
          deferred.reject(err);
        });
    };
    renderingReporter.start('starting datasets polling');
    this.observabilityService.startChildSpan(
      ObservabilityEvents.LEGACY_POLLING_LOOP,
      ObservabilityEvents.QUERY_RUN,
      Date.now()
    );
    poll(reportRunModel);
    return deferred.promise;
  }

  // Similar to pollLoadedReportRun, meant to reload dataRun and dataRunResults models after a cell export/exported cell run
  private refreshLoadedReportRun(reportRunModel: any, datasetToken?: string) {
    const deferred = this.$q.defer<ReportRunResource>();
    const queryRunFields = difference(QUERY_RUN_RESOURCE_PROPERTIES, ['raw_source', 'rendered_source']);
    this.loadDataRuns(reportRunModel._resource, {
      view: ['attrs', 'new_attr'],
      query_runs: { only: queryRunFields },
    })
      .then((queryRuns: any[]) => {
        const throttledLoadResult = concur((resource: QueryRunResource) => this.loadQueryRunResult(resource), 3);
        queryRuns.forEach((queryRunModel) => {
          const resource = queryRunModel._resource as QueryRunResource;
          if (!datasetToken || resource.query_token === datasetToken) {
            this.loadedQueryResults.set(resource.query_token, {
              queryRunResultPromise: throttledLoadResult(resource),
              queryRunToken: resource.token,
            });
          }
          this.datasetService.queryRuns = keyBy(queryRuns, 'query_token');
          this.datasetService.dismissed = {};
        });

        const resultPromise = this.loadedQueryResults.get(datasetToken).queryRunResultPromise;
        this.$q.when(resultPromise).then((results) => {
          this.reportFilterService.updateModels();
          this.reportStateService.setDatasets(this.datasetService.datasets);
          this.datasetService.loadRawDatasets(results.filter((ds) => ds != null));
        });
      })
      .catch((err) => {
        deferred.reject(err);
      });

    return deferred.promise;
  }

  public cancelPollReportRun() {
    this.store.dispatch({
      type: ReportRunActionType.CancelPollReportRun,
    });
  }

  public get reportRun() {
    return combineLatest([
      this.editorFacade.selectedQueryToken$,
      this.store.select('client', 'selectedReport', 'queries'),
      this.store.select('client', 'selectedReport', 'reportRun'),
      this.store.select('client', 'selectedReport', 'chartModels'),
      this.store.select('client', 'selectedReport', 'queryRuns'),
    ]).pipe(
      map(([queryToken, queries, reportRun, modelStates, queryRuns]) => ({
        queryToken: queryToken,
        query: queries.getRecord(queryToken),
        queries: queries,
        reportRun: reportRun,
        modelState: modelStates.hasRecord(queryToken) ? modelStates.getRecord(queryToken) : {},
        dataset:
          modelStates.hasRecord(queryToken) && modelStates.getRecord(queryToken).model
            ? modelStates.getRecord(queryToken).model.engine.dataset
            : {},
        queryRuns: queryRuns,
        queryRun: queryRuns.getRecord(queryToken),
      }))
    );
  }

  public get reportRunState$() {
    return combineLatest([
      this.store.select('client', 'selectedReport', 'queries'),
      this.store.select('client', 'selectedReport', 'reportRun'),
      this.store.select('client', 'selectedReport', 'chartModels'),
      this.store.select('client', 'selectedReport', 'queryRuns'),
    ]).pipe(
      map(([queries, reportRun, modelStates, queryRuns]) => ({
        queries: queries,
        reportRun: reportRun,
        models: modelStates,
        queryRuns: queryRuns,
      }))
    );
  }

  public get selectedQueryReportRunState$() {
    return combineLatest([
      this.editorFacade.selectedQueryToken$,
      this.store.select('client', 'selectedReport', 'queries'),
      this.store.select('client', 'selectedReport', 'reportRun'),
      this.store.select('client', 'selectedReport', 'chartModels'),
      this.store.select('client', 'selectedReport', 'queryRuns'),
    ]).pipe(
      map(([queryToken, queries, reportRun, modelStates, queryRuns]) => ({
        queryToken: queryToken,
        query: queries.getRecord(queryToken),
        queries: queries,
        reportRun: reportRun,
        modelStates: modelStates,
        modelState: modelStates.hasRecord(queryToken) ? modelStates.getRecord(queryToken) : ({} as ModelState),
        queryRuns: queryRuns,
        queryRun: queryRuns.getRecord(queryToken),
      }))
    );
  }

  public get queryRunComplete$() {
    return this.actions$.pipe(
      ofType(ReportRunActionType.LoadQueryResultSuccess),
      merge(this.actions$.pipe(ofType(ReportRunActionType.LoadQueryResultFailure))),
      map((a) => a.queryRun)
    );
  }

  public get selectedTableState$() {
    return combineLatest([
      this.editorFacade.selectedQueryToken$,
      this.store.select('client', 'selectedReport', 'tables'),
      this.store.select('client', 'selectedReport', 'chartModels'),
      this.store.select('client', 'selectedReport', 'reportRun'),
      this.store.select('client', 'selectedReport', 'queryRuns'),
    ]).pipe(
      map(([queryToken, tables, modelStates, reportRun, queryRuns]) => {
        const table = tables.getRecords().find((t) => t.queryToken === queryToken);
        if (table) {
          return {
            queryToken: table.queryToken,
            modelState: modelStates.hasRecord(table.queryToken) ? modelStates.getRecord(table.queryToken) : {},
            reportRun: reportRun,
            queryRun: queryRuns.getRecord(table.queryToken),
            table: table,
          };
        }

        return {
          reportRun: reportRun,
        };
      })
    );
  }

  public get canCancelSelectedQueryRun$() {
    return this.store.select(SelectedReportSelectors.canCancelSelectedQueryRun);
  }

  public cancelPolling() {
    this.store.dispatch({
      type: ReportRunActionType.CancelPollReportRun,
    });
  }

  public cancel(token: string) {
    this.store.dispatch({
      type: ReportRunActionType.CancelQueryRun,
      token,
    });
  }

  // helper for submitting report runs via page level forms on the report show page
  // this can be cleaned up once report runs do not require page refreshes
  public runReport(lastSuccessfulRunToken: string, reportRunsLink: string) {
    recordEvent('run-report', 'run-report-click', {
      runsLink: reportRunsLink,
    });
    formSubmit(reportRunsLink, {
      fields: {
        last_run_id: lastSuccessfulRunToken,
        authenticity_token: getCsrfToken(this.$window.document),
        trk_source: 'refresh_button',
      },
    });
  }

  private loadQueryRunResult(queryRunResource: QueryRunResource) {
    const { query_token } = queryRunResource;
    if (queryRunResource.state === 'succeeded') {
      recordEvent('legacy-poll-report-run', 'load-query-result', {
        traceId: this.currentRunToken,
        queryToken: query_token,
        queryRunToken: queryRunResource.token,
      });
      const queryRunModel = new this.QueryRunModel.model(queryRunResource);

      return queryRunModel
        .result()
        .then((qrrModel) => {
          const queryRunResultResource = qrrModel._resource;
          // @todo will need w/y starts
          const view = QueryRunResourceHelpers.makeView(queryRunResource, queryRunResultResource);

          this.store.dispatch(new LoadQueryRunView(queryRunResource, queryRunResultResource, view));

          recordEvent('legacy-poll-report-run', 'load-query-result-success', {
            traceId: this.currentRunToken,
            queryToken: query_token,
            queryRunToken: queryRunResource.token,
            overSized: qrrModel.overSized,
          });
          if (qrrModel.csv && !qrrModel.oversized) {
            recordEvent('legacy-poll-report-run', 'download-legacy-dataset', {
              traceId: this.currentRunToken,
              queryToken: query_token,
              queryRunToken: queryRunResource.token,
            });
            return qrrModel.dataset().then((dataset: DatasetResource) => {
              const { columns } = queryRunResultResource;
              const schema = VegasHelpers.schema(columns);
              const model = Model.build(schema, dataset.content);
              recordEvent('legacy-poll-report-run', 'download-legacy-dataset-success', {
                traceId: this.currentRunToken,
                queryToken: query_token,
                queryRunToken: queryRunResource.token,
                columns,
                size: dataset.bytes,
              });

              this.dispatchQueryResultSuccess(queryRunResource, queryRunResultResource, view, dataset, model);
              this.datasetService.models[query_token] = this.buildModelResult(dataset);
              // TODO: one day we should load in datasets as they come in once the singletons are gone
              // Currently this is done after this function in the .all promise so filtered datasets can be made
              return dataset;
            });
          } else if (qrrModel.oversized) {
            const model = Model.build(VegasHelpers.schema([]), []);
            const dataset = this.buildOversizedDataset(queryRunResource, queryRunResultResource);

            this.dispatchQueryResultSuccess(queryRunResource, queryRunResultResource, view, dataset, model);
            this.datasetService.models[query_token] = this.buildModelResult(dataset);
            return dataset;
          } else {
            this.dispatchQueryResultSuccess(queryRunResource, queryRunResultResource, view);

            return null;
          }
        })
        .catch((err) => {
          this.$log.log('error getting queryRunModel result', err);
          recordEvent('legacy-poll-report-run', 'load-query-result-failed', {
            traceId: this.currentRunToken,
            queryToken: query_token,
            queryRunToken: queryRunResource.token,
            err,
          });
          BugsnagHelpers.notifySafely(err);
        });
    } else {
      recordEvent('legacy-poll-report-run', 'load-query-result-skipped', {
        traceId: this.currentRunToken,
        queryToken: query_token,
        queryRunToken: queryRunResource.token,
        queryRunState: queryRunResource.state,
      });
      this.store.dispatchThunk(
        SelectedReportSelectors.createQueryResourceSelector(queryRunResource.query_token),
        (query) => ({
          type: ReportRunActionType.LoadQueryResultFailure,
          queryRun: queryRunResource,
          queryResource: query,
        })
      );
    }
    const dataset = this.buildFailedDataset(queryRunResource);

    this.datasetService.datasets[query_token] = dataset;
    this.datasetService.models[query_token] = this.buildModelResult(dataset);
    return this.$q.resolve(dataset);
  }

  private buildModelResult(dataset) {
    const result = {
      token: '', // irrelevant, only the query_token matters
      isLoaded: true,
      queryName: null,
      state: dataset.state,
      columns: dataset.columns,
      content: dataset.content,
      count: dataset.count,
      bytes: dataset.bytes,
      query_token: dataset.query_token,
      reportDataUrl: dataset.reportDataUrl,
      reportQueryUrl: dataset.reportQueryUrl,
      content_length: dataset.content_length,
      csv: dataset.csv,
      oversized: dataset.oversized,
      model: Model.build(VegasHelpers.schema([]), []),
    };

    if (!dataset.state) {
      result.state = 'succeeded';
      result.model = Model.build(Model.convertModeSchema(dataset.columns), dataset.content);
    }

    return result;
  }

  private buildOversizedDataset(queryRun: QueryRunResource, result: QueryResultResource) {
    return {
      token: '',
      columns: [],
      content_length: result.content_length,
      content: [],
      csv: result.hasLink('csv') ? result.links('csv').href : null,
      count: result.count,
      query_token: queryRun.query_token,
      state: queryRun.state,
      reportDataUrl: queryRun.hasLink('result_web') ? queryRun.links('result_web').href : undefined,
      reportQueryUrl: queryRun.hasLink('query_web') ? queryRun.links('query_web').href : undefined,
    };
  }

  private buildFailedDataset(queryRun: QueryRunResource) {
    return {
      query_token: queryRun.query_token,
      state: queryRun.state,
      reportDataUrl: queryRun.hasLink('result_web') ? queryRun.links('result_web').href : undefined,
      reportQueryUrl: queryRun.hasLink('query_web') ? queryRun.links('query_web').href : undefined,
    };
  }

  private trackRunEvent(isPublic) {
    const eventName = AnalyticsService.EVENT_PRIVATE_EDITOR_RUN;

    if (isPublic === false) {
      this.analyticsService.run(RunMethodType.Track, eventName);
    }
  }

  private dispatchQueryResultSuccess(
    queryRun: QueryRunResource,
    result: QueryResultResource,
    view: FlamingoTypes.ModeView,
    dataset?: Dataset,
    model?: Model
  ) {
    this.store.dispatchThunk(SelectedReportSelectors.createQueryResourceSelector(queryRun.query_token), (query) => ({
      type: ReportRunActionType.LoadQueryResultSuccess,
      queryRun: queryRun,
      resource: result,
      queryResource: query,
      dataset,
      model,
      view,
    }));
  }

  private dispatchQueryRunSuccess(qrResource: QueryRunResource, queryToken: string) {
    this.store.dispatchThunk(SelectedReportSelectors.createQueryResourceSelector(queryToken), (query) => ({
      type: ReportRunActionType.LoadQueryRunSuccess,
      resource: qrResource,
      mapping_id: query?.mapping_id,
    }));
  }
}
